import { useCallback, useEffect, useState } from "react";
import { getDatasetTypes } from "../services/datasetTypes";

/**
 * The dataset type registry, for the type selector and default lookups.
 *
 * @param {object} [options]
 * @param {boolean} [options.includeInactive]
 * @param {boolean} [options.enabled]
 */
export const useDatasetTypes = ({ includeInactive = false, enabled = true } = {}) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setTypes([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    setLoading(true);

    getDatasetTypes({ includeInactive })
      .then((result) => {
        if (cancelled) return;

        if (result?.success === false) {
          setTypes([]);
          setError(result.message || "Could not load the dataset types.");
        } else {
          setTypes(result.dataset_types ?? []);
          setError(null);
        }

        setLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setTypes([]);
        setError(loadError?.message || "Could not load the dataset types.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [includeInactive, enabled, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const byPublicId = useCallback(
    (publicId) => types.find((type) => type.public_id === publicId) ?? null,
    [types]
  );

  return { types, loading, error, reload, byPublicId };
};

export default useDatasetTypes;
